// What the analytics events say about the plan (AL-15098, Jet CR 72–74): read off the subscription and
// the commitment's own rules in src/cancelFlow/, never off the screens' copy. Plain functions, no store.
import type { CancellationOption, Subscription } from '@/api/medBilling'
import { commitmentKind, commitmentQuotes, termMonths, type CommitmentKind } from '@/cancelFlow/commitment'

export type PlanPaymentType = 'monthly' | 'paid_in_full'

function planPaymentType(kind: CommitmentKind | null): PlanPaymentType {
	return kind === 'upfront' ? 'paid_in_full' : 'monthly'
}

/** Payments made in the term: `iteration_count − remain_iterations`; paid in full — the whole term. */
function paymentsPaid(sub: Subscription, kind: CommitmentKind, months: number | null): number | null {
	if (kind === 'upfront') return months
	const count = commitmentQuotes(sub).iterationCount
	const remain = sub.remain_iterations ?? sub.phases?.find((p) => p.is_active)?.remain_iterations
	return count != null && remain != null ? Math.max(count - remain, 0) : null
}

/**
 * `SubscriptionDetailsScreenView` (CR 72): the plan, and on a commitment its term, payments and balance.
 * The plan is billing's `supply_schedule` (med-billing-engine#319): `term_type` the kind (`commitment` →
 * monthly, `upfront` → paid in full, `regular` → neither — the legacy 12w/24w plans too), `term_months`
 * the term, `supplies_shipped` the supplies out of the pharmacy.
 */
export function planDetails(
	sub: Subscription,
	underReview: boolean,
): {
	is_commitment: boolean
	plan_payment_type: PlanPaymentType
	plan_months?: number
	commitment_end_date?: number
	commitment_orders_shipped?: number
	commitment_payments_paid?: number
	commitment_balance_usd?: string
} {
	const kind = commitmentKind(sub)
	const months = termMonths(sub)
	const base = {
		is_commitment: Boolean(kind),
		plan_payment_type: planPaymentType(kind),
		plan_months: months ?? undefined,
	}
	if (!kind) return base
	const endIso = kind === 'upfront' ? sub.current_period_end_at : sub.commitment_end_at
	const end = endIso ? new Date(endIso).getTime() : NaN
	const balance = kind === 'monthly' && !underReview ? (sub.cancellation_options.instant_fee?.amount ?? 0) : 0
	const paid = paymentsPaid(sub, kind, months)
	const shipped = sub.supply_schedule?.supplies_shipped
	return {
		...base,
		...(Number.isNaN(end) ? {} : { commitment_end_date: end }),
		...(shipped != null ? { commitment_orders_shipped: shipped } : {}),
		...(paid != null ? { commitment_payments_paid: paid } : {}),
		commitment_balance_usd: balance.toFixed(2),
	}
}

/**
 * `expiration_date` (CR 72, agreed with analytics 2026-10-21): the backend's `expired_at` as it is — the
 * planned end of access, written at the cancel, the 14-day renewal grace included (none after `hard`).
 * `undefined` while the plan is not cancelled.
 */
export function expirationDate(sub: Subscription, cancelled: boolean): string | null | undefined {
	return cancelled ? sub.expired_at : undefined
}

/**
 * `data.commitment` on WHEN_TO_CANCEL and the two summaries (CR 73 / 74). `months_received` and the
 * amounts are the offered option's own (`shipped_months`, `amount`, `refund`) — what the fee or refund
 * was priced from; `fee_usd` is `instant_fee.amount`, what ending today costs, on every one of them —
 * the instalments' summary too (CR 73) — and 0 where it isn't offered (paid in full).
 */
export interface CommitmentStepData {
	plan_payment_type: PlanPaymentType
	plan_months: number | null
	payments_paid: number | null
	months_received: number | null
	/** Money as decimal strings ("408.00"), agreed with analytics 2026-10-21. */
	fee_usd: string
	refund_usd?: string
	payments_left?: number
}

/** Months of the supplies out of the pharmacy: each covers `term_months / supplies_total` (#319) — 3 on C12, 1 on B3. */
function monthsShipped(sub: Subscription, months: number | null): number | null {
	const schedule = sub.supply_schedule
	if (!schedule || months == null || !schedule.supplies_total) return null
	return Math.round((schedule.supplies_shipped * months) / schedule.supplies_total)
}

export function commitmentStepData(
	sub: Subscription | null,
	kind: CommitmentKind | null,
	option: CancellationOption | null,
	amounts: { fee: number; refund?: number; paymentsLeft?: number },
): CommitmentStepData | undefined {
	if (!sub || !kind) return undefined
	const months = termMonths(sub)
	return {
		plan_payment_type: planPaymentType(kind),
		plan_months: months,
		payments_paid: paymentsPaid(sub, kind, months),
		months_received: option?.shipped_months ?? monthsShipped(sub, months),
		fee_usd: amounts.fee.toFixed(2),
		...(amounts.refund != null ? { refund_usd: amounts.refund.toFixed(2) } : {}),
		...(amounts.paymentsLeft != null ? { payments_left: amounts.paymentsLeft } : {}),
	}
}
